import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import AuditPopover from '../components/AuditPopover';
import { useCurrencies } from '../hooks/useCurrencies';
import { formatCariOption } from '../utils/cariFormat';
import SearchableSelect from '../components/SearchableSelect';
import CariCard from '../components/CariCard';
import ModuleOverlay from '../components/ModuleOverlay';
import QuickListManager from '../components/QuickListManager';
import DataTable from '../components/DataTable';
import { Button, Modal, FormField, ListPageHeader, FilterBar } from '../components/ui';
import { CardHero, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const cariLabel = (a) => `${a.code || ''} - ${a.title || ''}`;
const cariSubLabel = (a) => {
  const parts = [];
  if (a.phone || a.mobile) parts.push(a.phone || a.mobile);
  if (a.identity_type === 'individual') {
    if (a.id_number) parts.push('TC: ' + a.id_number);
  } else {
    if (a.tax_office) parts.push(a.tax_office);
    if (a.tax_number) parts.push(a.tax_number);
  }
  parts.push(a.currency_code || 'TRY');
  return parts.filter(Boolean).join(' | ');
};
const cariSearchKeys = ['code', 'title', 'phone', 'mobile', 'tax_number', 'id_number', 'tax_office'];

const TX_CONFIG = {
  opening: { label: 'Cari Açılış Fişleri',     color: '#7c3aed' },
  debit:   { label: 'Cari Borç Dekontları',    color: '#dc2626' },
  credit:  { label: 'Cari Alacak Dekontları',  color: '#16a34a' },
  virman:  { label: 'Cari Virman Dekontları',  color: '#0284c7' },
};

const LOCAL_CURRENCY = 'TRY';

const emptyTx = {
  date: new Date().toISOString().substring(0, 10),
  description: '', foreign_amount: '', reference_no: '', notes: '',
  account_id: '', target_account_id: '',
  tx_currency: 'TRY',
  tx_rate: '1',
  target_foreign_amount: '',
  target_tx_rate: '1',
  opening_direction: 'credit',
  personnel_id: '', special_code1: '', special_code2: '',
};

function fmt(n, cur) {
  return new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0) + (cur ? ' ' + cur : '');
}
function fmtDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('tr-TR');
}

// Seçili cari için borç/alacak özeti — CariCard altında küçük rozet halinde gösterilir.
function BalancePill({ account }) {
  const bal = parseFloat(account?.balance) || 0;
  const cur = account?.currency_code || 'TRY';
  const isDebit = bal > 0;     // pozitif → cari bize borçlu
  const isCredit = bal < 0;    // negatif → biz cariye borçluyuz (cari alacaklı)
  const color = isDebit ? '#dc2626' : isCredit ? '#16a34a' : '#64748b';
  const bg    = isDebit ? 'rgba(220,38,38,0.10)' : isCredit ? 'rgba(22,163,74,0.10)' : 'rgba(100,116,139,0.08)';
  const label = isDebit ? 'Cari bize borçlu' : isCredit ? 'Cari bizden alacaklı' : 'Bakiye sıfır';
  const dir   = isDebit ? '🔴 BORÇ' : isCredit ? '🟢 ALACAK' : '⚪ KAPALI';
  return (
    <div style={{
      marginTop: 8, padding: '8px 12px', borderRadius: 8,
      background: bg, border: `1px solid ${color}33`,
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12,
    }}>
      <span style={{ fontWeight: 700, color, letterSpacing: '0.04em' }}>{dir}</span>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontWeight: 700, color, fontSize: 14 }}>
        {fmt(Math.abs(bal))} {cur}
      </span>
    </div>
  );
}

export default function CariVoucherPage({ txType, autoOpenId = null }) {
  const cfg = TX_CONFIG[txType] || TX_CONFIG.credit;
  const isVirman = txType === 'virman';
  const isOpening = txType === 'opening';
  const { currencies } = useCurrencies();

  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [specialCodes1, setSpecialCodes1] = useState([]);
  const [specialCodes2, setSpecialCodes2] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [filters, setFilters] = useState({ date_from: '', date_to: '', account_id: '' });

  const [modalMode, setModalMode] = useState(null);
  const [selectedTx, setSelectedTx] = useState(null);
  const [txForm, setTxForm] = useState({ ...emptyTx });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [quickAddTarget, setQuickAddTarget] = useState(null);
  const [showAccountList, setShowAccountList] = useState(null); // 'source' | 'target' | null
  const [accountListSearch, setAccountListSearch] = useState('');

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: txType };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/accounts/cari-transactions', { params });
      if (res.data.success) setTransactions(res.data.data || []);
    } catch (err) {
      console.error('cari-transactions error:', err.response?.data || err.message);
    } finally { setLoading(false); }
  }, [txType, filters]);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await api.get('/accounts', { params: { limit: 500 } });
      if (res.data.success) setAccounts((res.data.data.items || []).filter(a => a.is_active));
    } catch {}
  }, []);

  const fetchPersonnel = useCallback(async () => {
    try {
      const res = await api.get('/personnel', { params: { limit: 200 } });
      if (res.data.success) setPersonnel((res.data.data.items || []).filter(p => p.is_active));
    } catch {}
  }, []);

  const fetchSpecialCodes = useCallback(async () => {
    try {
      const r = await api.get('/invoices/lookups/all');
      if (r.data.success) {
        setSpecialCodes1(r.data.data?.specialCodes1 || []);
        setSpecialCodes2(r.data.data?.specialCodes2 || []);
      }
    } catch {}
  }, []);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  // Deep-link: autoOpenId varsa o hareketi aç (transactions yüklendikten sonra)
  useEffect(() => {
    if (!autoOpenId) return;
    const tx = (transactions || []).find(t => t.id === autoOpenId);
    if (tx) openView(tx);
  }, [autoOpenId, transactions]);
  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);
  useEffect(() => { fetchPersonnel(); }, [fetchPersonnel]);
  useEffect(() => { fetchSpecialCodes(); }, [fetchSpecialCodes]);

  const [showQuickList, setShowQuickList] = useState(null);

  const fetchRateForCurrency = async (currencyCode, date) => {
    if (!currencyCode || currencyCode === LOCAL_CURRENCY) return '1';
    try {
      const res = await api.get('/exchange/rates/lookup', { params: { date, currency_code: currencyCode } });
      if (res.data.success && res.data.data) {
        const r = res.data.data;
        return String(r.mid_rate || r.buying_rate || r.selling_rate || '1');
      }
    } catch {}
    return '1';
  };

  const closeModal = () => { setModalMode(null); setSelectedTx(null); setMsg(null); };

  const openView = (tx) => {
    const hasForeign = !!tx.foreign_currency;
    const foreignAmt = parseFloat(tx.foreign_debit || 0) + parseFloat(tx.foreign_credit || 0);
    const accAmt = parseFloat(tx.debit || 0) + parseFloat(tx.credit || 0);
    const txCur = hasForeign ? tx.foreign_currency : (tx.currency_code || tx.account_currency || 'TRY');
    const displayAmt = hasForeign ? foreignAmt : accAmt;

    setTxForm({
      ...emptyTx,
      date: tx.date ? new Date(tx.date).toISOString().substring(0, 10) : emptyTx.date,
      description: tx.description || '',
      foreign_amount: String(displayAmt),
      reference_no: tx.reference_no || '',
      notes: tx.notes || '',
      account_id: tx.account_id,
      target_account_id: tx.linked_account_id || '',
      tx_currency: txCur,
      tx_rate: String(tx.exchange_rate || '1'),
      target_foreign_amount: tx.linked_debit != null ? String(tx.linked_debit) : '',
      opening_direction: parseFloat(tx.debit || 0) > 0 ? 'debit' : 'credit',
    });
    setSelectedTx(tx);
    setMsg(null);
    setModalMode('view');
  };

  const openAdd = () => {
    setTxForm({ ...emptyTx });
    setSelectedTx(null);
    setMsg(null);
    setModalMode('add');
  };

  const handleSourceAccountChange = (accountId) => {
    const acc = accounts.find(a => a.id === accountId);
    const cur = acc?.currency_code || 'TRY';
    setTxForm(prev => ({ ...prev, account_id: accountId, tx_currency: cur }));
    if (cur !== LOCAL_CURRENCY) {
      fetchRateForCurrency(cur, txForm.date).then(r => {
        setTxForm(prev => ({ ...prev, tx_rate: r }));
      });
    } else {
      setTxForm(prev => ({ ...prev, tx_rate: '1' }));
    }
  };

  const handleTargetAccountChange = async (accountId) => {
    const acc = accounts.find(a => a.id === accountId);
    const cur = acc?.currency_code || 'TRY';
    const rate = cur === LOCAL_CURRENCY ? '1' : await fetchRateForCurrency(cur, txForm.date);
    setTxForm(prev => ({ ...prev, target_account_id: accountId, target_tx_rate: rate }));
  };

  const handleTxCurrencyChange = async (newCur) => {
    const rate = newCur === LOCAL_CURRENCY ? '1' : await fetchRateForCurrency(newCur, txForm.date);
    setTxForm(prev => ({ ...prev, tx_currency: newCur, tx_rate: rate }));
  };

  const handleSave = async (mode = 'close') => {
    if (mode && typeof mode === 'object') mode = 'close';
    const { account_id, target_account_id, date, description, foreign_amount, tx_currency, tx_rate, target_foreign_amount, target_tx_rate } = txForm;
    if (!account_id) return setMsg({ type: 'error', text: 'Cari seçin.' });
    if (isVirman && !target_account_id && modalMode === 'add') return setMsg({ type: 'error', text: 'Hedef cari seçin.' });
    if (!date || !description || !foreign_amount) return setMsg({ type: 'error', text: 'Tarih, açıklama ve tutar zorunludur.' });
    setSaving(true); setMsg(null);
    try {
      const body = {
        date,
        description,
        tx_currency,
        tx_amount: parseFloat(foreign_amount),
        tx_rate: parseFloat(tx_rate) || 1,
        reference_no: txForm.reference_no || null,
        notes: txForm.notes || null,
      };
      if (isOpening) body.opening_direction = txForm.opening_direction;
      if (isVirman) {
        body.target_tx_rate = parseFloat(target_tx_rate) || 1;
        if (target_foreign_amount) body.target_tx_amount = parseFloat(target_foreign_amount);
      }
      body.personnel_id = txForm.personnel_id || null;
      body.special_code1 = txForm.special_code1 || null;
      body.special_code2 = txForm.special_code2 || null;

      if (modalMode === 'edit' && selectedTx) {
        await api.put('/accounts/' + selectedTx.account_id + '/cari-transactions/' + selectedTx.id, body);
      } else {
        body.transaction_type = txType;
        if (isVirman) body.target_account_id = target_account_id;
        await api.post('/accounts/' + account_id + '/cari-transactions', body);
      }
      await fetchTransactions();
      if (mode === 'close') closeModal();
      else if (mode === 'new') { setMsg({ type: 'success', text: 'Kaydedildi — yeni dekont' }); setTimeout(() => setMsg(null), 2000); }
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi.' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selectedTx) return;
    if (!window.confirm('Bu kaydı silmek istiyor musunuz?')) return;
    try {
      await api.delete('/accounts/' + selectedTx.account_id + '/cari-transactions/' + selectedTx.id);
      closeModal();
      fetchTransactions();
    } catch (err) {
      alert(err.response?.data?.message || 'Silinemedi.');
    }
  };

  const tf = (key, val) => setTxForm(prev => ({ ...prev, [key]: val }));

  const sourceAccount = accounts.find(a => a.id === txForm.account_id);
  const targetAccount = accounts.find(a => a.id === txForm.target_account_id);

  const txAmount = parseFloat(txForm.foreign_amount) || 0;
  const txRate = parseFloat(txForm.tx_rate) || 1;
  const ypbAmount = txForm.tx_currency === LOCAL_CURRENCY ? txAmount : txAmount * txRate;

  const filtered = transactions.filter(tx =>
    !search ||
    (tx.doc_number || '').toLowerCase().includes(search.toLowerCase()) ||
    tx.description?.toLowerCase().includes(search.toLowerCase()) ||
    tx.account_title?.toLowerCase().includes(search.toLowerCase()) ||
    (tx.reference_no || '').toLowerCase().includes(search.toLowerCase())
  );

  const ro = modalMode === 'view';

  const txDisplayAmount = (tx) => {
    const f = parseFloat(tx.foreign_debit || 0) + parseFloat(tx.foreign_credit || 0);
    return f > 0 ? f : (parseFloat(tx.debit || 0) + parseFloat(tx.credit || 0));
  };
  const txDisplayCurrency = (tx) => tx.foreign_currency || tx.currency_code || tx.account_currency || 'TRY';
  const ypbDisplayAmount = (tx) => parseFloat(tx.debit_local || 0) + parseFloat(tx.credit_local || 0);
  const isDebitRow = (tx) => parseFloat(tx.debit || 0) > 0;

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const inView = modalMode === 'view';
  const isAdd = modalMode === 'add';
  const isEdit = modalMode === 'edit';
  const inEditAdd = isAdd || isEdit;

  const modalTitle = modalMode === 'add'
    ? 'Yeni: ' + cfg.label.replace('Cari ', '').replace('ları', '').replace('leri', '')
    : cfg.label.replace('Cari ', '').replace('ları', '').replace('leri', '');

  return (
    <div className="stocks-page">
      <ListPageHeader
        title={cfg.label}
        count={filtered.length}
        unit="kayıt"
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Ara..."
        onRefresh={fetchTransactions}
        refreshing={loading}
        actions={<Button size="sm" onClick={openAdd}>+ Yeni {isOpening ? 'Fiş' : 'Dekont'}</Button>}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'date_from', type: 'date', label: 'İlk Tarih' },
          { key: 'date_to',   type: 'date', label: 'Son Tarih' },
          { key: 'account_id', type: 'select', label: 'Cari', flex: '1 1 280px', minWidth: 240,
            options: [{ value: '', label: 'Tüm Cariler' }, ...accounts.map(a => ({ value: a.id, label: formatCariOption(a) }))] },
        ]}
      />

      {/* LİSTE */}
      <div className="stocks-table-wrap">
        <DataTable
          pageKey={`cari-voucher-${txType}-list`}
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt bulunamadı"
          data={filtered}
          rowKey="id"
          onRowClick={tx => openView(tx)}
          columns={[
            { id: 'doc_no', header: 'Belge No', accessor: 'doc_number', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, color: 'var(--accent-primary)', whiteSpace: 'nowrap' }}>{tx.doc_number || '—'}</span> },
            { id: 'date', header: 'Tarih', accessor: 'date', sortable: true, width: 110,
              sortFn: (a,b) => new Date(a.date||0)-new Date(b.date||0),
              cell: tx => <span style={{ whiteSpace: 'nowrap' }}>{fmtDate(tx.date)}</span> },
            { id: 'account', header: 'Cari', accessor: tx => (tx.account_title||'')+' '+(tx.account_code||''), sortable: true,
              cell: tx => <><div style={{ fontWeight: 600 }}>{tx.account_title}</div><div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{tx.account_code}</div></> },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: tx => <><div>{tx.description}</div>{tx.notes && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>{tx.notes}</div>}</> },
            ...(isVirman ? [
              { id: 'target_account', header: 'Hedef Cari', accessor: 'linked_account_title', sortable: true, width: 160,
                cell: tx => <span className="stock-secondary"><div>{tx.linked_account_title || '—'}</div>{tx.linked_account_currency && tx.linked_account_currency !== tx.account_currency && <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{tx.linked_account_currency}</div>}</span> },
            ] : []),
            { id: 'ref_no', header: 'Ref No', accessor: 'reference_no', sortable: true, width: 130,
              cell: tx => <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'var(--text-tertiary)' }}>{tx.reference_no || '—'}</span> },
            { id: 'amount', header: 'İşlem Dövizi Tutar', accessor: tx => txDisplayAmount(tx)||0, sortable: true, width: 160,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' },
              cell: tx => { const sign = isDebitRow(tx) ? '' : '+'; const rowColor = isVirman ? '#dc2626' : (isDebitRow(tx) ? '#dc2626' : '#16a34a'); return <span style={{ color: rowColor }}>{sign}{fmt(txDisplayAmount(tx), txDisplayCurrency(tx))}</span>; } },
            { id: 'ypb', header: 'YPB Tutar', accessor: tx => ypbDisplayAmount(tx)||0, sortable: true, width: 130,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.82rem' },
              cell: tx => fmt(ypbDisplayAmount(tx), 'TRY') },
            ...(isVirman ? [
              { id: 'linked_amount', header: 'Hedef Tutar', accessor: tx => parseFloat(tx.linked_debit)||0, sortable: true, width: 140,
                headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap' },
                cell: tx => tx.linked_debit != null ? '+' + fmt(tx.linked_debit, tx.linked_account_currency) : '—' },
            ] : []),
            { id: 'audit', header: '', sortable: false, hideable: false, draggable: false, filterable: false, width: 32,
              cellStyle: { textAlign: 'center' },
              cell: tx => <span onClick={e => e.stopPropagation()}><AuditPopover endpoint={`/accounts/cari-transactions/${tx.id}/audit`} /></span> },
          ]}
        />
      </div>

      {/* MODAL */}
      {modalMode !== null && (
        <Modal
          icon={null}
          title={modalTitle}
          size="lg"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {inView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {inEditAdd && <Button variant="ghost" onClick={isEdit ? () => { setMsg(null); setModalMode('view'); if (selectedTx) openView(selectedTx); } : closeModal}>{isAdd ? 'Vazgeç' : 'İptal'}</Button>}
                {inEditAdd && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç', icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',  icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {inView && <Button onClick={() => { setMsg(null); setModalMode('edit'); }}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {modalMode !== 'add' && selectedTx && (
            <CardHero
              icon="👤"
              code={selectedTx.doc_number}
              title={selectedTx.account_title || 'Cari Hareketi'}
              subtitle={selectedTx.date ? new Date(selectedTx.date).toLocaleDateString('tr-TR') : null}
              kpis={[
                txDisplayAmount(selectedTx) != null && {
                  label: 'Tutar', value: fmt(txDisplayAmount(selectedTx), txDisplayCurrency(selectedTx)),
                  color: 'primary', sublabel: txDisplayCurrency(selectedTx),
                },
              ].filter(Boolean)}
            />
          )}
          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>

            <FormField label="Belge No">
              <input disabled value={modalMode === 'add' ? 'Otomatik' : (selectedTx?.doc_number || '—')}
                style={{ fontFamily: 'monospace', opacity: 0.7 }} />
            </FormField>

            <FormField label="Tarih" required={!ro}>
              <input type={ro ? 'text' : 'date'} disabled={ro}
                value={ro ? fmtDate(txForm.date) : txForm.date}
                onChange={e => tf('date', e.target.value)} />
            </FormField>

            {/* CARI SEÇİMİ — Belge No ve Tarih'in altında, tam genişlik */}
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField label={isVirman ? 'Kaynak Cari' : 'Cari'} required={!ro}>
                <CariCard
                  cari={sourceAccount}
                  disabled={ro || modalMode === 'edit'}
                  label={isVirman ? 'Kaynak Cari' : 'Cari'}
                  required={!ro}
                  onPick={() => { setAccountListSearch(''); setShowAccountList('source'); }}
                />
                {sourceAccount && <BalancePill account={sourceAccount} />}
              </FormField>
            </div>

            {isVirman ? (
              <div style={{ gridColumn: '1 / -1' }}>
                <FormField label="Hedef Cari" required={!ro}>
                  <CariCard
                    cari={targetAccount}
                    disabled={ro || modalMode === 'edit'}
                    label="Hedef Cari"
                    required={!ro}
                    onPick={() => { setAccountListSearch(''); setShowAccountList('target'); }}
                  />
                  {targetAccount && <BalancePill account={targetAccount} />}
                </FormField>
              </div>
            ) : isOpening ? (
              <FormField label="Yön" required={!ro}>
                <select disabled={ro} value={txForm.opening_direction} onChange={e => tf('opening_direction', e.target.value)}>
                  <option value="debit">Borç (Cari bize borçlu)</option>
                  <option value="credit">Alacak (Cari bizden alacaklı)</option>
                </select>
              </FormField>
            ) : null}

            {/* İŞLEM DÖVİZİ */}
            <FormField label="İşlem Dövizi" required={!ro}>
              {ro ? (
                <input disabled value={txForm.tx_currency} />
              ) : (
                <select value={txForm.tx_currency} onChange={e => handleTxCurrencyChange(e.target.value)}>
                  {currencies.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name || c.code}</option>)}
                </select>
              )}
            </FormField>

            {/* İŞLEM KURU */}
            <FormField label={`Kur — 1 ${txForm.tx_currency} = ? TRY`}>
              <input type={ro ? 'text' : 'number'} min="0" step="0.000001"
                disabled={ro || txForm.tx_currency === LOCAL_CURRENCY}
                value={txForm.tx_rate}
                onChange={e => tf('tx_rate', e.target.value)} />
            </FormField>

            {/* İŞLEM DÖVİZİ TUTARI */}
            <FormField label={`İşlem Dövizi Tutar (${txForm.tx_currency})`} required={!ro}>
              <input type={ro ? 'text' : 'number'} min="0" step="0.01"
                disabled={ro}
                placeholder={ro ? '' : '0.00'}
                value={ro ? fmt(txForm.foreign_amount) : txForm.foreign_amount}
                onChange={e => tf('foreign_amount', e.target.value)} />
            </FormField>

            {/* YPB TUTAR */}
            <FormField label="YPB Tutar (TRY)">
              <input disabled value={fmt(ypbAmount)} style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent-primary)' }} />
            </FormField>

            {/* VİRMAN: Hedef tutar */}
            {isVirman && targetAccount && <>
              <FormField label={`Hedef Kur — 1 ${targetAccount.currency_code || 'TRY'} = ? TRY`}>
                <input type={ro ? 'text' : 'number'} min="0" step="0.000001"
                  disabled={ro || (targetAccount.currency_code || 'TRY') === LOCAL_CURRENCY}
                  value={txForm.target_tx_rate}
                  onChange={e => tf('target_tx_rate', e.target.value)} />
              </FormField>
              <FormField label={`Hedef Tutar (${targetAccount.currency_code || 'TRY'})`}>
                <input type={ro ? 'text' : 'number'} min="0" step="0.01"
                  disabled={ro}
                  placeholder={(() => {
                    const tr = parseFloat(txForm.target_tx_rate) || 1;
                    const suggested = (targetAccount.currency_code || 'TRY') === LOCAL_CURRENCY ? ypbAmount : ypbAmount / tr;
                    return suggested > 0 ? suggested.toFixed(2) : '0.00';
                  })()}
                  value={ro ? fmt(txForm.target_foreign_amount) : txForm.target_foreign_amount}
                  onChange={e => tf('target_foreign_amount', e.target.value)} />
              </FormField>
            </>}

            {/* ORTAK ALANLAR */}
            <FormField label="Açıklama" required={!ro} fullWidth>
              <input disabled={ro} value={txForm.description} onChange={e => tf('description', e.target.value)} placeholder={ro ? '' : 'İşlem açıklaması'} />
            </FormField>
            <FormField label="Referans No">
              <input disabled={ro} value={txForm.reference_no} onChange={e => tf('reference_no', e.target.value)} placeholder={ro ? '' : 'Opsiyonel'} />
            </FormField>
            <FormField label="Not">
              <input disabled={ro} value={txForm.notes} onChange={e => tf('notes', e.target.value)} placeholder={ro ? '' : 'Opsiyonel'} />
            </FormField>
            <FormField label="Personel">
              <select value={txForm.personnel_id || ''} onChange={e => tf('personnel_id', e.target.value)} disabled={ro}>
                <option value="">Seçiniz</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + p.last_name)}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 1 {!ro && <button className="quick-add-btn" onClick={() => setShowQuickList('special1')} type="button">+</button>}</>}>
              <select value={txForm.special_code1 || ''} onChange={e => tf('special_code1', e.target.value)} disabled={ro}>
                <option value="">— Seçiniz —</option>
                {specialCodes1.map(s => (
                  <option key={s.id} value={s.code || s.name}>
                    {s.code ? s.code + ' - ' : ''}{s.name}
                  </option>
                ))}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 2 {!ro && <button className="quick-add-btn" onClick={() => setShowQuickList('special2')} type="button">+</button>}</>}>
              <select value={txForm.special_code2 || ''} onChange={e => tf('special_code2', e.target.value)} disabled={ro}>
                <option value="">— Seçiniz —</option>
                {specialCodes2.map(s => (
                  <option key={s.id} value={s.code || s.name}>
                    {s.code ? s.code + ' - ' : ''}{s.name}
                  </option>
                ))}
              </select>
            </FormField>
          </div>
        </Modal>
      )}

      {quickAddTarget !== null && (
        <ModuleOverlay
          type="cari"
          onClose={() => setQuickAddTarget(null)}
          onSaved={async (rec) => {
            await fetchAccounts();
            if (rec?.id) {
              if (quickAddTarget === 'source') handleSourceAccountChange(rec.id);
              else handleTargetAccountChange(rec.id);
            }
          }}
        />
      )}

      {/* CARI LİSTESİ MODALI — fatura sayfasıyla aynı */}
      {showAccountList && (() => {
        const target = showAccountList; // 'source' | 'target'
        const q = (accountListSearch || '').toLowerCase().trim();
        const filtered = (accounts || []).filter(a =>
          (target !== 'target' || a.id !== txForm.account_id) &&
          (!q ||
            (a.code || '').toLowerCase().includes(q) ||
            (a.title || '').toLowerCase().includes(q) ||
            (a.tax_number || '').includes(q) ||
            (a.id_number || '').includes(q) ||
            (a.phone || '').includes(q) ||
            (a.mobile || '').includes(q)));
        const cariCols = [
          { id: 'code',  header: 'Kod',   accessor: 'code',  sortable: true, width: 110,
            cell: a => <span className="stock-code">{a.code}</span> },
          { id: 'title', header: 'Ünvan', accessor: 'title', sortable: true,
            cell: a => <span className="stock-name-cell">{a.title}</span> },
          { id: 'type',  header: 'Tip',   accessor: 'type',  sortable: true, width: 100 },
          { id: 'tax',   header: 'VD / TC', sortable: true, width: 150,
            accessor: a => a.identity_type === 'individual' ? (a.id_number || '') : `${a.tax_office || ''} ${a.tax_number || ''}`,
            cell: a => <span className="stock-secondary" style={{ fontSize:'0.78rem' }}>
              {a.identity_type === 'individual' ? (a.id_number || '—') : (a.tax_office || a.tax_number ? `${a.tax_office || ''}${a.tax_office && a.tax_number ? ' / ' : ''}${a.tax_number || ''}` : '—')}
            </span> },
          { id: 'phone', header: 'Telefon', sortable: true, width: 130,
            accessor: a => a.phone || a.mobile || '',
            cell: a => <span className="stock-secondary">{a.phone || a.mobile || '—'}</span> },
          { id: 'balance', header: 'Bakiye', accessor: a => parseFloat(a.balance) || 0, sortable: true, width: 170,
            headerStyle: { textAlign:'right' }, cellStyle: { textAlign:'right' },
            cell: a => {
              const b = parseFloat(a.balance) || 0;
              const color = b > 0 ? 'var(--danger)' : b < 0 ? 'var(--success)' : 'var(--text-tertiary)';
              const sfx = b > 0 ? ' B' : b < 0 ? ' A' : '';
              return <span style={{ fontFamily:'monospace', color, fontWeight: 600 }}>
                {fmt(Math.abs(b))} {a.currency_code || 'TRY'}{sfx}
              </span>;
            } },
        ];
        const pick = (a) => {
          if (target === 'source') handleSourceAccountChange(a.id);
          else handleTargetAccountChange(a.id);
          setShowAccountList(null);
        };
        return (
          <div className="modal-overlay" onClick={() => setShowAccountList(null)}>
            <div className="modal-box" style={{ maxWidth: 980, width: '100%', maxHeight: '85vh', display:'flex', flexDirection:'column', padding: 0 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border-secondary)', display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                <h3 style={{ margin: 0, fontSize:'1rem', fontWeight: 700 }}>Cari Listesi {target === 'target' && '(Hedef)'}</h3>
                <span style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius: 12, background:'var(--accent-glow)', color:'var(--accent-primary)', fontWeight: 600 }}>{filtered.length} kayıt</span>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <input autoFocus type="text" placeholder="Kod, ünvan, VD/TC, telefon ile ara..."
                    value={accountListSearch} onChange={e => setAccountListSearch(e.target.value)}
                    style={{ width:'100%', padding:'7px 12px', borderRadius: 8, border:'1px solid var(--border-secondary)', background:'var(--bg-secondary)', color:'var(--text-primary)', fontSize:'0.85rem', boxSizing:'border-box', outline:'none' }}
                  />
                </div>
                <button className="btn-primary-sm" onClick={() => { setShowAccountList(null); setQuickAddTarget(target); }}
                        style={{ display:'flex', alignItems:'center', gap: 4 }}>+ Yeni Cari</button>
                <button onClick={() => setShowAccountList(null)} title="Kapat"
                        style={{ width: 32, height: 32, padding: 0, display:'inline-flex', alignItems:'center', justifyContent:'center', border:'1px solid var(--border-secondary)', borderRadius: 8, background:'var(--bg-card)', cursor:'pointer', color:'var(--text-secondary)' }}>
                  ✕
                </button>
              </div>
              <div style={{ flex: 1, overflow:'auto' }} className="stocks-table-wrap">
                <DataTable
                  pageKey="cari-voucher-picker"
                  tableClassName="stocks-table"
                  rowClassName="stocks-table-row"
                  emptyText={q ? 'Eşleşen cari bulunamadı' : 'Henüz cari yok'}
                  data={filtered}
                  rowKey="id"
                  onRowClick={pick}
                  columns={cariCols}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {showQuickList && (
        <QuickListManager
          open={true}
          title={showQuickList === 'special1' ? 'Özel Kod 1' : 'Özel Kod 2'}
          items={showQuickList === 'special1' ? specialCodes1 : specialCodes2}
          apiBase="/invoices/special-codes"
          extraBody={{ code_type: showQuickList }}
          onClose={() => setShowQuickList(null)}
          onChanged={fetchSpecialCodes}
        />
      )}
    </div>
  );
}
